
/**
 * TWSE OpenAPI Service - Taiwan Stock Exchange Official API Integration
 * 
 * This service provides access to Taiwan Stock Exchange (TWSE) data
 * using their official OpenAPI endpoints.
 * 
 * Key Features:
 * - Real-time stock prices (all stocks)
 * - Market indices data
 * - Trading statistics
 * - Completely free (no API key required)
 * - Official OpenAPI compliance
 * 
 * Official API Documentation:
 * Base URL: https://openapi.twse.com.tw/v1/
 * Swagger: https://openapi.twse.com.tw/v1/swagger.json
 * All responses include CORS headers for frontend usage
 */

import { logger } from '../utils/logger';
import { ErrorHandler } from '../utils/errorHandler';
import { RetryHelper } from '../utils/retryHelper';
import { errorMonitoring } from '../utils/errorMonitoring';
import corsProxyService from './corsProxyService';
import { stockDataCache, marketDataCache } from '../utils/cacheService';

/**
 * Official TWSE OpenAPI Response Interfaces
 */

// Stock day data response from /exchangeReport/STOCK_DAY_ALL
interface TwseStockDayItem {
  Date: string;          // "1140814" (Taiwan calendar format)
  Code: string;          // Stock code
  Name: string;          // Stock name
  TradeVolume: string;   // Trading volume
  TradeValue: string;    // Trading value
  OpeningPrice: string;  // Opening price
  HighestPrice: string;  // Highest price
  LowestPrice: string;   // Lowest price
  ClosingPrice: string;  // Closing price
  Change: string;        // Price change
  Transaction: string;   // Transaction count
}

// Market index response from /exchangeReport/MI_INDEX
interface TwseMarketIndexItem {
  日期: string;      // Date
  指數: string;      // Index name
  收盤指數: string;  // Closing index
  漲跌: string;      // Up/Down indicator
  漲跌點數: string;  // Change points
  漲跌百分比: string; // Change percentage
  特殊處理註記: string; // Special notes
}

/**
 * Parsed stock data from TWSE OpenAPI
 */
export interface TwseStockData {
  date: string;           // ISO date format (YYYY-MM-DD)
  stockCode: string;      // Stock code
  stockName: string;      // Stock name
  volume: number;         // Trading volume
  turnover: number;       // Trading value
  openPrice: number;      // Opening price
  highPrice: number;      // Highest price
  lowPrice: number;       // Lowest price
  closePrice: number;     // Closing price
  change: number;         // Price change amount
  transaction: number;    // Transaction count
}

/**
 * Current stock price data from TWSE OpenAPI
 */
export interface TwseCurrentPrice {
  stockCode: string;
  stockName: string;
  volume: number;
  transaction: number;
  turnover: number;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  closePrice: number;
  change: number;         // Price change amount
  changePercent: number;  // Change percentage
}

/**
 * Market index data from TWSE OpenAPI
 */
export interface TwseIndexData {
  date: string;           // ISO date format
  indexName: string;      // Index name
  closeIndex: number;     // Closing index value
  change: '+' | '-' | '';  // Direction indicator
  changePoints: number;   // Change in points
  changePercent: number;  // Change percentage
  notes: string;          // Special notes
}


/**
 * Real-time OHLC data from TWSE MIS API
 */
export interface TwseRealtimeOHLC {
  c: string;    // 收盤價/最新價
  s: string;    // 成交量
  t: string;    // Unix timestamp
  ts: string;   // Time string (HHMMSS)
}

export interface TwseRealtimeResponse {
  userDelay: number;
  staticObj: {
    t4: string;   // Total volume
    tv: string;   // Total value
    tz: string;   // Total turnover
    t1: string;   // Buy orders
    key: string;  // Market key
    t2: string;   // Sell orders
    tr: string;   // Trade count
    t3: string;   // Others
  };
  ch: string;     // Channel (t00.tw for TAIEX)
  rtmessage: string;
  ohlcArray: TwseRealtimeOHLC[];
}

/**
 * Parsed intraday data point
 */
export interface IntradayDataPoint {
  time: string;
  index: number;
  volume?: number;
  timestamp?: number;
}

class TwseApiService {
  // 證交所官方 API (公開資訊，理論上應該支援 CORS)
  private readonly TWSE_DIRECT_URL = 'https://openapi.twse.com.tw/v1';
  
  // 使用統一緩存服務替代本地緩存
  
  // 無效股票代號快取 - 避免重複查詢
  private static invalidStockCache = new Map<string, number>(); // symbol -> 負快取到期時間(DATA-09 expiry-based)
  private static readonly INVALID_STOCK_CACHE_DURATION = 30 * 60 * 1000; // 30分鐘

  /**
   * 使用統一的 CORS 代理服務呼叫 API
   */
  private async corsProxyFetch(endpoint: string): Promise<Response> {
    logger.api.info('Calling TWSE API with unified CORS proxy service', { endpoint });
    
    const targetUrl = `${this.TWSE_DIRECT_URL}${endpoint}`;
    return await corsProxyService.fetch(targetUrl);
  }

  /**
   * Get all stocks daily trading data (Latest available data)
   * @returns Promise with all stocks trading data
   */
  async getAllStocksDayData(): Promise<TwseStockData[]> {
    logger.api.info('TwseApiService.getAllStocksDayData() called');
    
    const cacheKey = 'all_stocks_day_data';
    const cached = this.getCachedData(cacheKey);
    if (cached) {
      logger.api.info('Retrieved stocks day data from cache', { count: cached.length });
      return cached;
    }

    try {
      // 使用重試機制調用 API
      const stockData = await RetryHelper.apiRetry(async () => {
        logger.api.debug('Sending TWSE API request', { endpoint: '/exchangeReport/STOCK_DAY_ALL' });
        const response = await this.corsProxyFetch('/exchangeReport/STOCK_DAY_ALL');

        const data: TwseStockDayItem[] = await response.json();
        // DATA-07:驗證回應形狀(上游錯誤頁/物件會讓 .map 拋 TypeError)
        if (!Array.isArray(data)) {
          throw new Error(`TWSE STOCK_DAY_ALL response is not an array (got ${typeof data})`);
        }

        const parsedData = this.parseAllStocksData(data);
        logger.api.info('TWSE API data retrieved and parsed', { stockCount: parsedData.length });
        
        return parsedData;
      }, {
        maxRetries: 2, // 減少重試次數
        baseDelay: 5000, // 增加重試間隔到5秒
        onRetry: (error, attempt) => {
          // 只在最後一次重試時記錄詳細信息
          if (attempt === 2) {
            logger.api.warn(`TWSE API final retry attempt`, { 
              endpoint: '/exchangeReport/STOCK_DAY_ALL',
              error: error?.message 
            });
          }
        }
      });
      
      this.setCachedData(cacheKey, stockData);
      
      logger.api.info('TwseApiService.getAllStocksDayData() completed', { returnCount: stockData.length });
      return stockData;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'TwseApiService.getAllStocksDayData');
      logger.api.error('TwseApiService.getAllStocksDayData() failed', { error: appError });
      
      // 報告錯誤到監控系統
      errorMonitoring.reportError(error instanceof Error ? error : new Error(String(error)), {
        component: 'TwseApiService',
        category: 'api',
        severity: 'high',
        metadata: {
          method: 'getAllStocksDayData',
          endpoint: '/exchangeReport/STOCK_DAY_ALL'
        }
      });
      
      throw new Error(`Failed to fetch all stocks data: ${appError.message}`);
    }
  }

  /**
   * Get current stock prices for all listed stocks (from daily data)
   * @returns Promise with current stock prices
   */
  async getCurrentStockPrices(): Promise<TwseCurrentPrice[]> {
    logger.api.info('TwseApiService.getCurrentStockPrices() called');
    
    const cacheKey = 'current_stock_prices';
    const cached = this.getCachedData(cacheKey);
    if (cached) {
      logger.api.info('Retrieved stock prices from cache', { count: cached.length });
      return cached;
    }

    try {
      const stocksData = await this.getAllStocksDayData();
      const currentPrices = stocksData.map(stock => this.convertToCurrentPrice(stock));
      
      this.setCachedData(cacheKey, currentPrices);
      
      logger.api.info('TwseApiService.getCurrentStockPrices() completed', { returnCount: currentPrices.length });
      return currentPrices;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'TwseApiService.getCurrentStockPrices');
      logger.api.error('TwseApiService.getCurrentStockPrices() failed', { error: appError });
      throw new Error(`Failed to fetch current stock prices: ${appError.message}`);
    }
  }

  /**
   * Get a specific stock's current price
   * @param stockCode - Taiwan stock code
   * @returns Promise with current stock price data
   */
  async getStockPrice(stockCode: string): Promise<TwseCurrentPrice | null> {
    try {
      // 檢查是否為已知的無效股票(DATA-09:expiry-based,不為每個代號掛 timer)
      const expiry = TwseApiService.invalidStockCache.get(stockCode);
      if (expiry !== undefined) {
        if (expiry > Date.now()) {
          return null;
        }
        TwseApiService.invalidStockCache.delete(stockCode);
      }

      const allPrices = await this.getCurrentStockPrices();
      const result = allPrices.find(stock => stock.stockCode === stockCode) || null;

      if (!result) {
        // 將找不到的股票加入快取(記錄到期時間,30 分鐘後自然失效;
        // 「查無」可能只是假日/官方資料延遲,不應永久封鎖)
        TwseApiService.invalidStockCache.set(stockCode, Date.now() + TwseApiService.INVALID_STOCK_CACHE_DURATION);

        // 只日誌一次
        logger.api.info('Stock not found, added to invalid cache', { stockCode, totalStocks: allPrices.length });

        // 檢查是否為常見股票代號格式
        if (!/^\d{4}$/.test(stockCode)) {
          logger.api.debug('Invalid stock code format', { stockCode });
        }
      }

      return result;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'TwseApiService.getStockPrice');
      logger.api.error('Failed to fetch stock price', { stockCode, error: appError });
      return null;
    }
  }

  /**
   * Get market indices data
   * @returns Promise with market indices data
   */
  async getMarketIndices(): Promise<TwseIndexData[]> {
    const cacheKey = 'market_indices';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const response = await this.corsProxyFetch('/exchangeReport/MI_INDEX');
      const data: TwseMarketIndexItem[] = await response.json();
      // DATA-07:驗證回應形狀(比照 TPEx 的防禦)
      if (!Array.isArray(data)) {
        throw new Error(`TWSE MI_INDEX response is not an array (got ${typeof data})`);
      }
      const indices = this.parseMarketIndices(data);
      
      this.setCachedData(cacheKey, indices);
      return indices;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'TwseApiService.getMarketIndices');
      logger.api.error('Failed to fetch TWSE market indices', { error: appError });
      throw new Error(`Failed to fetch market indices: ${appError.message}`);
    }
  }

  /**
   * Check if TWSE OpenAPI is available
   * @returns Promise<boolean> indicating API availability
   */
  async isApiAvailable(): Promise<boolean> {
    try {
      const response = await this.corsProxyFetch('/exchangeReport/MI_INDEX');
      return response.ok;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'TwseApiService.isApiAvailable');
      logger.api.error('TWSE OpenAPI availability check failed', { error: appError });
      return false;
    }
  }

  // DATA-03:已移除 getRealTimeIntradayData 與 generateFallbackIntradayData —
  // 舊實作回傳 Math.random() 模擬的加權指數資料且無任何呼叫者,
  // 對投資系統而言,偽造行情資料比沒有資料更危險

  /**
   * Parse all stocks data from TWSE OpenAPI response
   */
  private parseAllStocksData(data: TwseStockDayItem[]): TwseStockData[] {
    return data.map(item => ({
      date: this.parseTaiwanDate(item.Date),
      stockCode: item.Code,
      stockName: item.Name,
      volume: parseInt(item.TradeVolume.replace(/,/g, '') || '0'),
      turnover: parseInt(item.TradeValue.replace(/,/g, '') || '0'),
      openPrice: parseFloat(item.OpeningPrice || '0'),
      highPrice: parseFloat(item.HighestPrice || '0'),
      lowPrice: parseFloat(item.LowestPrice || '0'),
      closePrice: parseFloat(item.ClosingPrice || '0'),
      change: parseFloat(item.Change || '0'),
      transaction: parseInt(item.Transaction.replace(/,/g, '') || '0')
    }));
  }

  /**
   * Convert TwseStockData to TwseCurrentPrice format
   */
  private convertToCurrentPrice(stock: TwseStockData): TwseCurrentPrice {
    const changePercent = stock.closePrice !== 0 ? (stock.change / stock.closePrice) * 100 : 0;
    
    return {
      stockCode: stock.stockCode,
      stockName: stock.stockName,
      volume: stock.volume,
      transaction: stock.transaction,
      turnover: stock.turnover,
      openPrice: stock.openPrice,
      highPrice: stock.highPrice,
      lowPrice: stock.lowPrice,
      closePrice: stock.closePrice,
      change: stock.change,
      changePercent: Number(changePercent.toFixed(2))
    };
  }

  /**
   * Parse market indices from TWSE OpenAPI response
   */
  private parseMarketIndices(data: TwseMarketIndexItem[]): TwseIndexData[] {
    return data.map(item => ({
      date: this.parseTaiwanDate(item.日期),
      indexName: item.指數,
      closeIndex: parseFloat(item.收盤指數.replace(/,/g, '') || '0'),
      change: item.漲跌 as '+' | '-' | '',
      changePoints: parseFloat(item.漲跌點數 || '0'),
      changePercent: parseFloat(item.漲跌百分比 || '0'),
      notes: item.特殊處理註記
    }));
  }

  /**
   * Convert Taiwan date format to ISO format
   * Supports both "113/08/14" and "1140814" formats
   */
  private parseTaiwanDate(taiwanDate: string): string {
    // Handle different date formats from TWSE API
    let year: string, month: string, day: string;
    
    if (taiwanDate.includes('/')) {
      // Format: "113/08/14"
      [year, month, day] = taiwanDate.split('/');
    } else if (taiwanDate.length === 7) {
      // Format: "1140814" (3-digit year + 2-digit month + 2-digit day)
      year = taiwanDate.substring(0, 3);
      month = taiwanDate.substring(3, 5);
      day = taiwanDate.substring(5, 7);
    } else {
      // Log only unexpected formats to avoid spam
      if (process.env.NODE_ENV === 'development') {
        logger.api.warn('Unrecognized date format', { taiwanDate });
      }
      // Fallback: try to parse as YYYYMMDD format
      if (taiwanDate.length === 8) {
        const fullYear = taiwanDate.substring(0, 4);
        month = taiwanDate.substring(4, 6);
        day = taiwanDate.substring(6, 8);
        return `${fullYear}-${month}-${day}`;
      }
      // DATA-07:解析失敗回空字串 — 硬編碼 '2024-01-01' 會污染歷史資料
      return '';
    }
    
    // Validate parsed components
    if (!year || !month || !day) {
      // Only log errors to avoid excessive logging
      logger.api.error('Date parsing failed', { taiwanDate, year, month, day });
      return '';
    }
    
    const westernYear = parseInt(year) + 1911; // Taiwan calendar starts from 1911
    const result = `${westernYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    
    // Only log in development mode and for debugging
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
      logger.api.info('Taiwan date parsing sample', { taiwanDate, result });
    }
    
    return result;
  }

  /**
   * Get current date string in YYYYMMDD format
   */
  private getCurrentDateString(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  /**
   * Get cached data if available and not expired
   */
  private getCachedData(key: string): any | null {
    // 根據資料類型選擇適當的緩存
    if (key.includes('market')) {
      return marketDataCache.get(key);
    }
    return stockDataCache.get(key);
  }

  /**
   * Set data in cache with timestamp
   */
  private setCachedData(key: string, data: any): void {
    // 根據資料類型選擇適當的緩存
    if (key.includes('market')) {
      marketDataCache.set(key, data);
    } else {
      stockDataCache.set(key, data);
    }
  }

  /**
   * DATA-06:僅清除 TWSE 自己的快取項 —
   * stockDataCache 為跨服務共享(TPEx/Yahoo 也使用),全清會誤傷其他服務的有效資料
   */
  public clearCache(): void {
    stockDataCache.delete('all_stocks_day_data');
    stockDataCache.delete('current_stock_prices');
    marketDataCache.delete('market_indices');
  }

  /**
   * 重設連接方式偏好，強制重新嘗試所有代理
   */
  public resetConnectionPreference(): void {
    corsProxyService.resetConnectionPreference();
    logger.api.info('Reset CORS proxy preferences through unified proxy service');
  }

  /**
   * 取得目前使用的 CORS 代理
   */
  public getCurrentConnectionMethod(): string | null {
    return corsProxyService.getCurrentConnectionMethod();
  }

  /**
   * 手動設定首選的 CORS 代理
   */
  public setPreferredConnectionMethod(method: 'allorigins' | 'x2u' | 'codetabs' | 'direct'): void {
    corsProxyService.setPreferredConnectionMethod(method);
    logger.api.info('Set preferred CORS proxy through unified proxy service', { method });
  }

  /**
   * 取得服務狀態訊息
   */
  public getServiceStatus() {
    const proxyStatus = corsProxyService.getServiceStatus();
    
    return {
      ...proxyStatus,
      stockCacheSize: stockDataCache.size(),
      marketCacheSize: marketDataCache.size(),
      directEndpoint: this.TWSE_DIRECT_URL
    };
  }


  /**
   * Generate intraday simulation data
   * @param closingIndex - Closing index value
   * @param change - Change amount
   * @returns Simulated intraday data
   */
  generateIntradayData(closingIndex: number, change: number): Array<{time: string, index: number}> {
    const data = [];
    const openingIndex = closingIndex - change;
    
    // Generate Taiwan stock trading hours data (9:00-13:30)
    for (let i = 0; i < 54; i++) { // 5-minute intervals, 54 points total
      const hour = Math.floor(i / 12) + 9;
      const minute = (i % 12) * 5;
      
      // Stop after 13:30
      if (hour > 13 || (hour === 13 && minute > 30)) {
        break;
      }
      
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      // Generate realistic intraday movement based on closing data
      const progress = i / 53;
      const baseValue = openingIndex + (change * progress);
      
      // Add small random variation to simulate intraday volatility
      const volatility = Math.abs(change) * 0.1;
      const randomVariation = (Math.random() - 0.5) * volatility;
      
      const indexValue = baseValue + randomVariation;
      
      data.push({
        time,
        index: Math.round(indexValue * 100) / 100
      });
    }
    
    return data;
  }
}

export const twseApiService = new TwseApiService();
export default TwseApiService;
